// The request-to-book proposal page (/secure/request/<proposal-id>): a
// school proposes a change to a customer's booking request, and the
// customer accepts it (and pays, when there's money due) or says it doesn't
// work. A port of v1's requestProposalFormat + the decisions its
// RequestProposalClient made inline. Pure.
//
// Payloads mirror the API's public routes: GET
// /widget/pages/request-proposals/:id (GetRequestProposalPageController),
// POST …/accept, POST …/decline. Dates arrive as ISO strings.

import { formatCurrency } from "~/widgets/lib/utils";
import { BookingApiError } from "../../api";
import { safeTimezone } from "../../timezones";
import { isValidDate } from "../../dates";

/** The proposal's EFFECTIVE status: a PENDING proposal past its deadline
 * reads EXPIRED, and one the booking no longer waits on reads WITHDRAWN. */
export type RequestProposalStatus = "PENDING" | "ACCEPTED" | "DECLINED" | "EXPIRED" | "WITHDRAWN";

/** Why a proposal was withdrawn (WITHDRAWN only). */
export type RequestProposalWithdrawnReason = "partner" | "target_unavailable" | "target_changed" | "request_ended" | "orphan";

/** After an accept: where the customer stands with the money.
 * - processing: the accept stopped part-way; a repeat Accept resumes it.
 * - contact_school: accepted, but the booking isn't in a payable state the
 *   page can link to, and a repeat Accept would be refused. */
export type RequestProposalPaymentState = "pay" | "nothing_to_pay" | "processing" | "contact_school" | "cancelled";

export type RequestProposalPlace = {
  /** "" when the listing could not be read. */
  listingTitle: string;
  startDateTime: string | null;
  endDateTime: string | null;
  /** Human-readable ("Online", a venue's name or address, or ""). */
  location: string;
};

export type RequestProposalLine = {
  /** "ticket" | "addOn" */
  kind: string;
  label: string;
  quantity: number;
  unitPricePence: number;
};

export type RequestProposalDepositHeld = {
  /** Money already held on the request (net of refunds). */
  paidPence: number;
  /** What would remain owed after the move (PENDING) / owed now (otherwise). */
  outstandingPence: number;
  /** The top-up due on accept (0 = none). */
  topUpPence: number;
  nextDueDate: string | null;
};

export type RequestProposalPayload = {
  proposalId: string;
  status: RequestProposalStatus;
  canRespond: boolean;
  withdrawnReason: RequestProposalWithdrawnReason | null;
  school: { name: string | null; slug: string | null; timezone: string | null; logoUrl: string | null };
  customer: { firstName: string | null };
  requested: RequestProposalPlace;
  proposed: RequestProposalPlace;
  lines: RequestProposalLine[];
  currency: string;
  totalPence: number;
  chargeNowPence: number;
  /** Null on a deposit-held proposal (no link on accept). */
  payByDate: string | null;
  depositHeld: RequestProposalDepositHeld | null;
  partnerMessage: string | null;
  customerNote: string | null;
  expiresAt: string | null;
  respondedAt: string | null;
  /** ACCEPTED only; null otherwise. */
  paymentState: RequestProposalPaymentState | null;
  /** A path on the school's site (/secure/pay/booking/<id>), or null. */
  paymentUrl: string | null;
  nothingToPay: boolean;
};

export type AcceptRequestProposalResponse = {
  status: "ACCEPTED";
  /** Same values as the GET's paymentState (never "cancelled" here). */
  paymentState: RequestProposalPaymentState;
  paymentUrl: string | null;
  nothingToPay: boolean;
};

/** What the customer's answer echoes onto the page until the re-read lands. */
export type ProposalOverride = Partial<
  Pick<RequestProposalPayload, "status" | "canRespond" | "paymentState" | "customerNote" | "nothingToPay">
>;

export const CUSTOMER_NOTE_MAX_LENGTH = 1000;

const GENERIC_ERROR = "Something went wrong. Please try again in a moment.";

function zoned(iso: string, timezone: string | null | undefined) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: safeTimezone(timezone),
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(iso));
  const out: Record<string, string> = {};
  for (const part of parts) out[part.type] = part.value;
  return out;
}

/** "Saturday 10 October 2026 at 10:00", in the school's timezone, 24-hour:
 * the request-to-book emails' wording (`dddd D MMMM YYYY [at] HH:mm`). */
export function formatDateTime(iso: string | null | undefined, timezone: string | null | undefined): string | null {
  if (!isValidDate(iso)) return null;
  const p = zoned(iso, timezone);
  return `${p.weekday} ${p.day} ${p.month} ${p.year} at ${p.hour}:${p.minute}`;
}

/** "Saturday 10 October 2026 at 10:00–12:00", or both dates across days. */
export function formatWhen(
  place: Pick<RequestProposalPlace, "startDateTime" | "endDateTime">,
  timezone: string | null | undefined,
): string | null {
  const start = formatDateTime(place.startDateTime, timezone);
  if (!start || !isValidDate(place.startDateTime) || !isValidDate(place.endDateTime)) return start;
  const a = zoned(place.startDateTime, timezone);
  const b = zoned(place.endDateTime, timezone);
  const sameDay = a.year === b.year && a.month === b.month && a.day === b.day;
  return sameDay ? `${start}–${b.hour}:${b.minute}` : `${start} – ${formatDateTime(place.endDateTime, timezone)}`;
}

/** Pence as money, always with pence ("£60.00"), as v1's page showed it. */
export function formatProposalMoney(pence: number, currency: string | null | undefined): string {
  return formatCurrency(pence, currency || "GBP");
}

function instantOf(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/** Which parts of the booking the proposal changes. The time compares the
 * instants (start and end, null-safe), not their formatted text. */
export function changedParts(data: Pick<RequestProposalPayload, "requested" | "proposed">) {
  return {
    listing: !!data.proposed.listingTitle && data.requested.listingTitle !== data.proposed.listingTitle,
    when:
      instantOf(data.requested.startDateTime) !== instantOf(data.proposed.startDateTime) ||
      instantOf(data.requested.endDateTime) !== instantOf(data.proposed.endDateTime),
    location: (data.requested.location || "") !== (data.proposed.location || ""),
  };
}

/** The API's customer-facing wording, or a generic line (a network error
 * carries no body). */
export function errorMessageOf(error: unknown): string {
  if (error instanceof BookingApiError) {
    const message = error.uxMessage || error.message;
    if (message && message.trim()) return message;
  }
  return GENERIC_ERROR;
}

/** Whether a refused answer means the proposal moved under the customer
 * (answered elsewhere, expired, withdrawn, its target gone or changed): then
 * the page re-reads it. An accept still being applied and a rate limit are
 * worth a retry instead. */
export function shouldRefetchAfterError(error: unknown): boolean {
  if (!(error instanceof BookingApiError)) return false;
  if (error.appErrorType === "PROPOSAL_ACCEPT_IN_PROGRESS") return false;
  return error.status === 404 || error.status === 409 || error.status === 410;
}

/** Whether the customer can still answer. */
export function isPendingView(view: Pick<RequestProposalPayload, "status" | "canRespond">): boolean {
  return view.status === "PENDING" && view.canRespond;
}

/** How much of the money the page shows: all of it while the customer
 * decides and when there's something to pay; only the total once booked
 * with nothing due; none otherwise (the figures aren't actionable any more). */
export function moneyMode(view: Pick<RequestProposalPayload, "status" | "canRespond" | "paymentState">): "full" | "total" | null {
  if (isPendingView(view) || (view.status === "ACCEPTED" && view.paymentState === "pay")) return "full";
  if (view.status === "ACCEPTED" && view.paymentState === "nothing_to_pay") return "total";
  return null;
}

export type MoneyRow = { term: string; value: string; strong?: boolean };

/** The money summary's rows. */
export function moneyRows(view: RequestProposalPayload, totalOnly: boolean): MoneyRow[] {
  const money = (pence: number) => formatProposalMoney(pence, view.currency);
  const tz = view.school.timezone;
  const rows: MoneyRow[] = [{ term: "Total", value: money(view.totalPence), strong: true }];
  if (totalOnly) return rows;
  if (view.depositHeld) {
    const held = view.depositHeld;
    rows.push({ term: "Deposit already paid", value: money(held.paidPence) });
    if (held.topUpPence > 0) rows.push({ term: "Due now", value: money(held.topUpPence), strong: true });
    if (held.outstandingPence > 0) {
      const due = formatDateTime(held.nextDueDate, tz);
      rows.push({ term: due ? `Remaining balance (due ${due})` : "Remaining balance", value: money(held.outstandingPence) });
    }
    return rows;
  }
  rows.push({ term: "Due now", value: money(view.chargeNowPence), strong: true });
  const payBy = formatDateTime(view.payByDate, tz);
  if (payBy && view.chargeNowPence > 0) rows.push({ term: "Pay by", value: payBy });
  return rows;
}

/** A booking line's figure: its unit price times its quantity. */
export function lineTotal(line: RequestProposalLine, currency: string): string {
  return formatProposalMoney(line.unitPricePence * line.quantity, currency);
}

/** Why a withdrawn proposal can't be answered, in the customer's words. */
export function withdrawnExplanation(reason: RequestProposalWithdrawnReason | null, schoolName: string): string {
  switch (reason) {
    case "target_unavailable":
    case "target_changed":
      return `The option ${schoolName} proposed is no longer available, so it can't be accepted. Please contact ${schoolName} if you have any questions.`;
    case "partner":
      return `${schoolName} has withdrawn this proposal. If they suggest something else, you'll receive a new email.`;
    case "request_ended":
      return `Your booking request is no longer active, so this proposal can't be answered. Please contact ${schoolName} if you have any questions.`;
    default:
      return `It can't be answered any more. Please contact ${schoolName} if you have any questions.`;
  }
}

/** What an accept leads to: off to the pay page, or the accepted state
 * echoed in place (with what to announce) until the re-read lands. */
export function acceptOutcome(
  result: Pick<AcceptRequestProposalResponse, "paymentState" | "paymentUrl" | "nothingToPay">,
): { kind: "pay"; paymentUrl: string } | { kind: "stay"; override: ProposalOverride; announcement: string } {
  if (result.paymentUrl) return { kind: "pay", paymentUrl: result.paymentUrl };
  return {
    kind: "stay",
    override: { status: "ACCEPTED", canRespond: false, paymentState: result.paymentState, nothingToPay: result.nothingToPay },
    announcement: result.paymentState === "contact_school" ? "Your acceptance was received." : "Thanks — change accepted.",
  };
}

/** The decline's body: the trimmed note, when there is one. */
export function declineBody(note: string): { note?: string } {
  const trimmed = note.trim();
  return trimmed ? { note: trimmed } : {};
}

/** The state the page's messages belong to: an error or announcement made
 * under one state is stale once the server's state moves on. */
export function serverKeyOf(data: Pick<RequestProposalPayload, "status" | "paymentState">): string {
  return `${data.status}|${data.paymentState ?? ""}`;
}

/** Whether an announcement still applies: it was made under the current
 * state, or the state moved to exactly the answer it announced. */
export function announcementApplies(
  announcement: { key: string; echoed?: RequestProposalStatus } | null,
  data: Pick<RequestProposalPayload, "status" | "paymentState">,
): boolean {
  if (!announcement) return false;
  return announcement.key === serverKeyOf(data) || announcement.echoed === data.status;
}

/** The page as shown: the customer's answer echoed over the server's state
 * while the server still reads PENDING (the answer went through, so a
 * PENDING read is one that started before it), and the server's own state
 * once it has moved on. */
export function withEcho(data: RequestProposalPayload, echo: ProposalOverride | null): RequestProposalPayload {
  return echo && data.status === "PENDING" ? { ...data, ...echo } : data;
}
